import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Play, Pause, RotateCcw, Square, Clock, CalendarDays, AlertCircle } from 'lucide-react';
import { getMyTimerToday, sendTimerAction } from '../../services/api';

const formatTime = (seconds = 0) => {
  const safe = Number.isFinite(seconds) ? Math.floor(Math.max(0, seconds)) : 0;
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const liveSecondsFromSync = (baseSeconds, status, activeStatus, syncedAt) => {
  const base = Number(baseSeconds) || 0;
  if (status !== activeStatus) return base;
  const elapsed = Math.floor((Date.now() - syncedAt) / 1000);
  return base + Math.max(0, elapsed);
};

const TimerManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [sync, setSync] = useState({ work: 0, break: 0, at: Date.now(), status: 'offline' });
  const [, setTick] = useState(0);

  const status = sync.status;

  const statusText = useMemo(() => {
    if (status === 'online') return 'В работе';
    if (status === 'break') return 'На перерыве';
    return 'Не в смене';
  }, [status]);

  const applyTimerResponse = (response) => {
    setData(response);
    setSync({
      work: response?.today_work_time || 0,
      break: response?.today_break_time || 0,
      at: Date.now(),
      status: response?.user?.status || 'offline',
    });
    setError('');
  };

  const fetchData = async () => {
    try {
      const response = await getMyTimerToday();
      applyTimerResponse(response);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось загрузить таймер');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const liveWorkSeconds = liveSecondsFromSync(sync.work, status, 'online', sync.at);
  const liveBreakSeconds = liveSecondsFromSync(sync.break, status, 'break', sync.at);

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      const response = await sendTimerAction(action);
      applyTimerResponse(response);
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка действия таймера');
    } finally {
      setActionLoading('');
    }
  };

  const events = data?.today_events || [];

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-4 sm:p-6 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter text-white flex items-center gap-3 italic uppercase">
            Tаймер
            </h1>
          <div className="flex items-center gap-2 mt-2 uppercase text-[10px] font-black tracking-[0.3em] text-indigo-400">
            <div className="h-1 w-6 bg-indigo-500 rounded-full" /> Учёт рабочего времени
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-2xl border bg-rose-500/10 border-rose-500/20 text-rose-400 flex items-center gap-2 text-sm font-bold">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex min-h-[320px] items-center justify-center rounded-[2.5rem] bg-[#1e293b]/40 border border-white/5">
            <Loader2 className="animate-spin text-indigo-500" size={40} />
          </div>
        ) : (
        <>
        <div className="bg-[#1e293b]/40 border border-white/5 rounded-2xl md:rounded-[2.5rem] p-4 sm:p-6 md:p-8 space-y-6 md:space-y-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Статус</p>
              <p className="text-2xl font-black text-white">{statusText}</p>
            </div>
            <div className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border ${
              status === 'online'
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : status === 'break'
                  ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                  : 'bg-slate-500/10 text-slate-400 border-white/10'
            }`}>
              {statusText}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-900/60 rounded-2xl p-5 border border-white/5">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Сегодня отработано</p>
              <p className="mt-2 text-3xl font-black text-white tabular-nums">{formatTime(liveWorkSeconds)}</p>
            </div>
            <div className="bg-slate-900/60 rounded-2xl p-5 border border-white/5">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Перерыв</p>
              <p className="mt-2 text-3xl font-black text-white tabular-nums">{formatTime(liveBreakSeconds)}</p>
            </div>
          </div>

          <div className="md:hidden p-4 rounded-2xl bg-slate-800/50 border border-white/10 text-center">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Управление таймером (начало, перерыв, завершение смены) доступно только с компьютера.
            </p>
          </div>

          <div className="hidden md:grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              disabled={status !== 'offline' || !!actionLoading}
              onClick={() => handleAction('start_work')}
              className="min-h-[48px] px-4 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2"
            >
              {actionLoading === 'start_work' ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
              Начать
            </button>

            <button
              disabled={(status !== 'online' && status !== 'break') || !!actionLoading}
              onClick={() => handleAction(status === 'break' ? 'break_end' : 'break_start')}
              className="px-4 py-4 rounded-2xl bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2"
            >
              {actionLoading === 'break_start' || actionLoading === 'break_end'
                ? <Loader2 size={16} className="animate-spin" />
                : status === 'break'
                  ? <RotateCcw size={16} />
                  : <Pause size={16} />
              }
              {status === 'break' ? 'Вернуться' : 'Перерыв'}
            </button>

            <button
              disabled={(status !== 'online' && status !== 'break') || !!actionLoading}
              onClick={() => handleAction('end_day')}
              className="px-4 py-4 rounded-2xl bg-rose-600 hover:bg-rose-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2"
            >
              {actionLoading === 'end_day' ? <Loader2 size={16} className="animate-spin" /> : <Square size={16} />}
              Завершить
            </button>
          </div>
        </div>

        <div className="bg-[#1e293b]/30 border border-white/5 rounded-2xl md:rounded-[2.5rem] p-4 sm:p-6 md:p-8">
          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <CalendarDays size={12} /> События за сегодня
          </p>
          {events.length === 0 ? (
            <p className="text-slate-500 text-sm">Пока нет событий за сегодня.</p>
          ) : (
            <div className="space-y-2">
              {events.map((event) => (
                <div key={event.id} className="flex items-center justify-between bg-black/20 border border-white/5 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-300">
                    <Clock size={14} className="text-indigo-400" />
                    {event.label}
                  </div>
                  <span className="text-[11px] font-black text-slate-500 tabular-nums">{event.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default TimerManager;
